import { fetchSignedFileUrl } from '@common/services/featherIO';
import { isCanvas } from '@common/utils';
import React from 'react';
import { PlayerCanvas } from './components/PlayerCanvas';
import { PlayerPreview } from './components/PlayerPreview';
import { getValueBinding } from '../../shared';

/**
 * A bound value may arrive as a bare URL or as the upload object a file column
 * stores, and `toString()` on the latter yields "[object Object]" - truthy
 * enough to pass the `!url` guard below and be handed to the player as a
 * source.
 */
const toUrlString = (value: any): string => {
  if (value && typeof value === 'object') {
    return value.url?.toString() || '';
  }
  return value?.toString() || '';
};

const AudioPlayer = (props: any) => {
  const _isCanvas = isCanvas();
  const bindingValue = getValueBinding(
    props.record ? props.initId : props.id,
    props.record ? props?.record : props.data,
    props
  );
  const title = bindingValue?.title?.toString() || '';
  const url = toUrlString(bindingValue?.url);
  const autoPlay = bindingValue?.autoPlay?.toString() === 'true';
  const downloadable = bindingValue?.downloadable?.toString() === 'true';

  // The stored URL is a pre-signed S3 link that expired five minutes after it
  // was saved, so it has to be signed again before the player is handed it.
  //
  // This has to gate the render rather than swap the source in later: both
  // players load at mount, not on the play button. The native one builds its
  // `Sound` in a mount effect and reads the track length out of the load
  // callback, and the web one uses `<audio preload="metadata">`. Once that
  // load fails on an expired URL no later tap recovers it.
  //
  // Signed through `fetchSignedFileUrl`, which treats any scheme-less value as
  // an S3 key, because that is what the download button in this same player
  // already does - a narrower test would leave playback broken for the
  // uploads that download fine.
  const [signedUrl, setSignedUrl] = React.useState('');

  React.useEffect(() => {
    if (_isCanvas || !url) {
      return;
    }
    let cancelled = false;
    setSignedUrl('');

    fetchSignedFileUrl(url)
      .then((fresh) => {
        if (!cancelled) setSignedUrl(fresh);
      })
      .catch(() => {
        // Hand the player the stored URL rather than leaving it empty
        // forever; an external link that is not ours to sign still plays.
        if (!cancelled) setSignedUrl(url);
      });

    return () => {
      cancelled = true;
    };
  }, [url, _isCanvas]);

  // `PlayerCanvas` is already what shows when there is nothing to play, and it
  // draws the same transport, so holding the first paint here costs a round
  // trip rather than a layout jump.
  if (_isCanvas || !url || !signedUrl) {
    return <PlayerCanvas {...props} />;
  }
  return (
    <PlayerPreview
      {...props}
      url={signedUrl}
      title={title}
      autoPlay={autoPlay}
      downloadable={downloadable}
    />
  );
};

export default AudioPlayer;
